import { useEffect, useRef, useState } from "react";
import { assistantOwnerId, initialsOf, isAssistantId } from "@worktools/shared";
import { api, type ShareCandidate } from "../api/client";
import type { JoinRequest, LiveBoard, LobbyConnection } from "../sync/lobby";
import { useUi } from "../state/ui";
import { useBoard } from "../sync/BoardContext";
import { useDialogs } from "../ui/dialogs";
import { IconChevron, IconLockOpen, IconShare, IconSpotlight, IconTrash } from "./icons";

export type Person = {
  id: string;
  name: string;
  color: string;
  avatarUrl: string | null;
  isMe: boolean;
  ownerName?: string;
};

export function Hud({
  participants,
  liveBoards,
  joinRequests,
  lobby,
  onLeaveBoard,
  onBoardsChanged,
  onSpotlight,
  blockCount,
}: {
  participants: Person[];
  liveBoards: LiveBoard[];
  joinRequests: JoinRequest[];
  lobby: LobbyConnection | null;
  onLeaveBoard: () => void;
  onBoardsChanged: () => Promise<unknown>;
  onSpotlight: () => void;
  blockCount: number;
}) {
  const { board, connected, me } = useBoard();
  const dialogs = useDialogs();
  const [menu, setMenu] = useState<"board" | "share" | null>(null);
  const [candidates, setCandidates] = useState<ShareCandidate[]>([]);
  const [search, setSearch] = useState("");
  const followingId = useUi((state) => state.followingId);
  const setFollowing = useUi((state) => state.setFollowing);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOwner = board.role === "owner";
  const isLive = liveBoards.some((entry) => entry.boardId === board.id);

  useEffect(() => {
    if (!menu) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenu(null);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menu]);

  useEffect(() => {
    if (menu !== "share") {
      setSearch("");
      return;
    }
    const timeout = setTimeout(
      () => {
        api
          .shareCandidates(board.id, search)
          .then(({ candidates: list }) => setCandidates(list))
          .catch(() => setCandidates([]));
      },
      search ? 150 : 0,
    );
    return () => clearTimeout(timeout);
  }, [menu, board.id, board.sharedWith.length, search]);

  const renameBoard = async () => {
    const name = await dialogs.prompt({ title: "Board umbenennen", label: "Neuer Name", value: board.name });
    if (!name) return;
    await api.renameBoard(board.id, name);
    await onBoardsChanged();
    setMenu(null);
    dialogs.toast("Board umbenannt");
  };

  const removeBoard = async () => {
    const confirmed = await dialogs.confirm({
      title: "Board löschen?",
      label: `„${board.name}“ und alle Blöcke darin werden endgültig entfernt.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!confirmed) return;
    if (isLive) lobby?.shareStop(board.id);
    await api.deleteBoard(board.id);
    await onBoardsChanged();
    onLeaveBoard();
  };

  const exportBoard = async () => {
    const data = await api.exportBoard(board.id);
    const text = JSON.stringify(data, null, 2);
    setMenu(null);
    try {
      await navigator.clipboard.writeText(text);
      dialogs.toast("Board als JSON in die Zwischenablage kopiert");
      return;
    } catch {
      // Discord blocks downloads inside the iframe; the clipboard is the reliable path.
    }
    try {
      const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${board.name}.json`;
      link.click();
      URL.revokeObjectURL(url);
      dialogs.toast("Board exportiert");
    } catch {
      dialogs.toast("Export nicht möglich — Discord blockiert Downloads");
    }
  };

  const toggleShare = () => {
    if (!isOwner) return;
    if (isLive) {
      lobby?.shareStop(board.id);
      dialogs.toast("Board ist nicht mehr im Channel sichtbar");
    } else {
      lobby?.shareStart(board.id, board.name);
      dialogs.toast("Board ist jetzt im Channel sichtbar");
    }
  };

  const labelFor = (person: Person) => {
    if (!isAssistantId(person.id)) {
      return person.isMe ? `${person.name} (du)` : `${person.name} — folgen`;
    }
    return assistantOwnerId(person.id) === me.id
      ? "Dein Claude ist verbunden"
      : `Claude von ${person.ownerName ?? "jemandem"}`;
  };

  return (
    <div className="hud">
      <div className="pillbar pillbar--round">
        <button className="pillbtn pillbtn--icon" onClick={onLeaveBoard} title="Zurück zur Übersicht">
          <IconChevron size={15} style={{ transform: "rotate(180deg)" }} />
        </button>
      </div>

      <div className="pillbar" ref={menu === "board" ? menuRef : undefined}>
        <div className="pillbar__title">
          <b>{board.name}</b>
          <span>{isOwner ? "Dein Board" : `von ${board.ownerName || "unbekannt"}`}</span>
        </div>
        {isOwner && (
          <button
            className="pillbtn pillbtn--icon"
            onClick={() => setMenu(menu === "board" ? null : "board")}
            title="Board-Einstellungen"
          >
            <IconChevron size={13} style={{ transform: "rotate(90deg)" }} />
          </button>
        )}
        {menu === "board" && (
          <div className="menu" style={{ top: 50, left: 0 }}>
            <button className="menu__item" onClick={renameBoard}>
              Umbenennen
            </button>
            <button
              className="menu__item"
              onClick={async () => {
                await api.duplicateBoard(board.id);
                await onBoardsChanged();
                setMenu(null);
                dialogs.toast("Board dupliziert");
              }}
            >
              Duplizieren
            </button>
            <button className="menu__item" onClick={exportBoard}>
              Als JSON exportieren
            </button>
            <div className="menu__sep" />
            <button className="menu__item menu__item--danger" onClick={removeBoard}>
              <IconTrash size={13} /> Board löschen
            </button>
          </div>
        )}
      </div>

      <div className="hud__spacer" />

      <div className="pillbar pillbar--status">
        <span className={`dot${connected ? "" : " dot--off"}`} />
        <span className="pillbar__status">
          {connected ? "Live" : "Verbindung…"} · {blockCount} {blockCount === 1 ? "Block" : "Blöcke"}
        </span>
      </div>

      <div className="pillbar" ref={menu === "share" ? menuRef : undefined}>
        {isOwner && (
          <button
            className={`pillbtn${isLive ? " pillbtn--live" : ""}`}
            onClick={toggleShare}
            title={isLive ? "Board aus dem Channel nehmen" : "Board für alle im Voice-Channel sichtbar machen"}
          >
            {isLive ? <span className="pulse" /> : <IconShare size={14} />}
            <span className="pillbtn__text">{isLive ? "Im Channel" : "Teilen"}</span>
          </button>
        )}

        {isOwner && (
          <button
            className="pillbtn"
            onClick={() => setMenu(menu === "share" ? null : "share")}
            title="Dauerhaft freigeben"
          >
            <IconLockOpen size={14} />
            <span className="pillbtn__text">Freigeben</span>
            {board.sharedWith.length > 0 && <span className="pillbtn__count">{board.sharedWith.length}</span>}
          </button>
        )}

        <button className="pillbtn pillbtn--accent" onClick={onSpotlight} title="Alle auf meinen Ausschnitt lenken">
          <IconSpotlight size={14} />
          <span className="pillbtn__text">Fokus lenken</span>
        </button>

        {menu === "share" && (
          <div className="menu menu--wide" style={{ top: 50, right: 0 }}>
            <div className="menu__label label">Dauerhaft freigegeben</div>
            {board.sharedWith.length === 0 && (
              <div className="menu__hint">Noch niemand. Freigegebene sehen das Board überall.</div>
            )}
            {board.sharedWith.map((member) => (
              <div key={member.userId} className="menu__row">
                <span className="avatar avatar--static" style={{ borderColor: "var(--line-strong)" }}>
                  {member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : initialsOf(member.name)}
                </span>
                <span className="menu__rowname">{member.name}</span>
                <button
                  className="row-action"
                  title="Freigabe entfernen"
                  onClick={async () => {
                    await api.removeShare(board.id, member.userId);
                    await onBoardsChanged();
                  }}
                >
                  <IconTrash size={13} />
                </button>
              </div>
            ))}

            <div className="menu__sep" />
            <div className="menu__label label">Person hinzufügen</div>
            <input
              type="text"
              className="menu__search"
              placeholder="Name suchen…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {candidates.length === 0 && (
              <div className="menu__hint">
                {search
                  ? "Kein Treffer. Gesucht wird unter allen, die schon mitgearbeitet haben."
                  : "Wer in einer Session mitgearbeitet hat, erscheint hier zum dauerhaften Freigeben."}
              </div>
            )}
            {candidates.map((candidate) => (
              <button
                key={candidate.userId}
                className="menu__row menu__row--action"
                onClick={async () => {
                  await api.addShare(board.id, {
                    userId: candidate.userId,
                    name: candidate.name,
                    avatarUrl: candidate.avatarUrl,
                  });
                  await onBoardsChanged();
                  setSearch("");
                  dialogs.toast(`${candidate.name} dauerhaft freigegeben`);
                }}
              >
                <span className="avatar avatar--static" style={{ borderColor: "var(--line-strong)" }}>
                  {candidate.avatarUrl ? <img src={candidate.avatarUrl} alt="" /> : initialsOf(candidate.name)}
                </span>
                <span className="menu__rowname">
                  {candidate.name}
                  <small>{candidate.source}</small>
                </span>
                <span className="shortcut">+</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="pillbar pillbar--people">
        {participants.map((person) => {
          const assistant = isAssistantId(person.id);
          return (
            <button
              key={person.id}
              className={[
                "avatar",
                followingId === person.id ? "avatar--following" : "",
                assistant ? "avatar--ai" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ borderColor: person.color }}
              title={labelFor(person)}
              onClick={() =>
                !person.isMe && !assistant && setFollowing(followingId === person.id ? null : person.id)
              }
            >
              {assistant ? (
                <img src="/claude-logo.svg" alt="Claude" />
              ) : person.avatarUrl ? (
                <img src={person.avatarUrl} alt="" />
              ) : (
                initialsOf(person.name)
              )}
            </button>
          );
        })}
      </div>

      {isOwner && joinRequests.length > 0 && (
        <div className="requests">
          {joinRequests.map((request) => (
            <div key={request.userId} className="requests__item">
              <span className="avatar avatar--static" style={{ borderColor: "var(--accent)" }}>
                {request.avatarUrl ? <img src={request.avatarUrl} alt="" /> : initialsOf(request.name)}
              </span>
              <span>
                <b style={{ fontWeight: 500 }}>{request.name}</b> möchte beitreten
              </span>
              <button onClick={() => lobby?.approve(board.id, request.userId)}>Zulassen</button>
              <button className="ghost" onClick={() => lobby?.deny(board.id, request.userId)}>
                Ablehnen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
